import React, { useEffect, useState, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { db, functions } from '../../../../firebase-client';
import { getFormFieldPlaceholder } from '../../../../utils/formFieldPresentation';

function BookingField({ field, value, onChange }) {
  const required = field.required === true;
  const requiredLabel = required ? ' (required)' : '';
  if (field.type === 'long_text') return <label>{field.label}{requiredLabel}<textarea className="input" rows="4" required={required} value={value || ''} onChange={(event) => onChange(event.target.value)} placeholder={getFormFieldPlaceholder(field)} />{field.helpText && <small>{field.helpText}</small>}</label>;
  if (field.type === 'single_choice') return <label>{field.label}{requiredLabel}<select className="input" required={required} value={value || ''} onChange={(event) => onChange(event.target.value)}><option value="">Choose…</option>{(field.options || []).map((option) => <option key={option}>{option}</option>)}</select>{field.helpText && <small>{field.helpText}</small>}</label>;
  if (field.type === 'multiple_choice') return <fieldset><legend>{field.label}</legend>{(field.options || []).map((option) => <label key={option} className="checkbox-row"><input type="checkbox" checked={(value || []).includes(option)} onChange={(event) => onChange(event.target.checked ? [...(value || []), option] : (value || []).filter((item) => item !== option))} /> {option}</label>)}</fieldset>;
  if (field.type === 'confirmation') return <label className="checkbox-row"><input type="checkbox" required={required} checked={value === true} onChange={(event) => onChange(event.target.checked)} /><span>{field.declarationWording || field.label}{requiredLabel}<small> Declaration version {field.declarationVersion}</small></span></label>;
  return <label>{field.label}{requiredLabel}<input className="input" required={required} type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'} value={value ?? ''} onChange={(event) => onChange(event.target.value)} placeholder={getFormFieldPlaceholder(field)} />{field.helpText && <small>{field.helpText}</small>}{field.sensitive && <small> Protected information; access is restricted and audited.</small>}</label>;
}

export default function TicketControls({
  status,
  busy,
  paidCount,
  pendingCount,
  startCheckout,
  ticketMeta,
  pricing,
  onRequestRefund,
  eventStartTime,     // 👈 add this (Date or Firestore Timestamp)
  manageDefaultOpen = false,
  refundRequested = false,
  coverageLabel = '',
  checkoutUnavailableMessage = '',
  payoutTimingText = '',
  loadError = '',
  admissionTicketsEnabled = false,
  bookingConfig = null,
}) {
  const [qty, setQty] = useState(1);
  const [manageOpen, setManageOpen] = useState(manageDefaultOpen);
  const [openSection, setOpenSection] = useState(null);
  const [ticketTypeId, setTicketTypeId] = useState(bookingConfig?.ticketTypes?.[0]?.id || ''); const [bookingAnswers, setBookingAnswers] = useState({}); const [attendeeAnswers, setAttendeeAnswers] = useState({}); const [extras, setExtras] = useState({}); const [saveProfileFields, setSaveProfileFields] = useState(false); const [resolvedBookingForm, setResolvedBookingForm] = useState(null);
  const bookingFormId = bookingConfig?.bookingForm?.formId || '';
  const bookingFormVersionId = bookingConfig?.bookingForm?.versionId || '';
  useEffect(() => {
    let active = true;
    if (!bookingConfig?.teamId || !bookingConfig?.eventId || !bookingFormId || !bookingFormVersionId) {
      setResolvedBookingForm(null);
      return () => { active = false; };
    }
    httpsCallable(functions, 'getEventBookingForm')({ teamId: bookingConfig.teamId, eventId: bookingConfig.eventId }).then(({ data }) => {
      if (active) setResolvedBookingForm(data?.form || null);
    }).catch(() => {
      if (active) setResolvedBookingForm(null);
    });
    return () => { active = false; };
  }, [bookingConfig?.teamId, bookingConfig?.eventId, bookingFormId, bookingFormVersionId]);
  const bookingFields = useMemo(() => resolvedBookingForm?.fields || [], [resolvedBookingForm]);
  const hasAttendeeFields = bookingFields.some((field) => field.scope === 'attendee');
  useEffect(() => {
    if (bookingFields.length === 0) return;
    const uid = getAuth().currentUser?.uid; if (!uid) return;
    Promise.all([getDoc(doc(db, 'users', uid)), getDoc(doc(db, 'privateUsers', uid))]).then(([user, privateUser]) => {
      const profile = { ...(user.data() || {}), ...(privateUser.data() || {}) }; const answers = {};
      bookingFields.filter((field) => field.scope === 'attendee' && field.profileBinding && !field.sensitive).forEach((field) => { if (profile[field.profileBinding] != null) answers[field.key] = profile[field.profileBinding]; });
      setAttendeeAnswers(answers);
    }).catch(() => {});
  }, [bookingFields]);

  const meta = ticketMeta || {};
  const { name, price, currency } = meta;

  const label =
    status === 'paid'
      ? `You’ve purchased ${paidCount} ticket${paidCount === 1 ? '' : 's'}`
      : status === 'pending'
      ? `Checkout pending (${pendingCount})`
      : status === 'refunded'
      ? 'Booking refunded'
      : 'No tickets yet';

  const isSkeleton = !ticketMeta && (status === 'idle' || status === undefined);

  // --- Detect past event ---
  const isPastEvent = (() => {
    if (!eventStartTime) return false;
    const start =
      eventStartTime instanceof Date
        ? eventStartTime
        : eventStartTime.toDate
        ? eventStartTime.toDate()
        : new Date(eventStartTime);
    return start.getTime() < Date.now();
  })();

  // --- Totals ---
  const totals = useMemo(() => {
    if (!pricing) return null;
    const mul = (n) => Number(n || 0) * Math.max(1, qty);
    const buyerPaysMinorTotal = mul(pricing.buyerPaysMinor);
    const stripeFeeMinorTotal = mul(pricing.stripeFeeMinor);
    const platformFeeMinorTotal = mul(pricing.platformFeeMinor);
    const unitMinorTotal = mul(pricing.unitMinor);

    const resolved = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: pricing.currency,
    }).resolvedOptions();
    const factor = (resolved.maximumFractionDigits || 0) === 0 ? 1 : 100;

    const fmt = (minor) =>
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: pricing.currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: resolved.maximumFractionDigits ?? 2,
      }).format((Number(minor) || 0) / factor);

    return {
      buyerPays: fmt(buyerPaysMinorTotal),
      unit: fmt(unitMinorTotal),
      stripe: fmt(stripeFeeMinorTotal),
      platform: fmt(platformFeeMinorTotal),
    };
  }, [pricing, qty]);

  // --- Purchase block (shared) ---
  const PurchaseBlock = (
    <>
      <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
        <label className="text-muted small" htmlFor="ticketQty">Quantity</label>
        <input
          id="ticketQty"
          type="number"
          min={1}
          step={1}
          max={hasAttendeeFields ? 1 : undefined}
          value={qty}
          onChange={(e) => setQty(hasAttendeeFields ? 1 : Math.max(1, parseInt(e.target.value || '1', 10)))}
          style={{ width: 80 }}
          disabled={isPastEvent || Boolean(checkoutUnavailableMessage)}
        />
      </div>

      {(bookingConfig?.ticketTypes || []).length > 0 && <label>Ticket type<select className="input" value={ticketTypeId} onChange={(event) => setTicketTypeId(event.target.value)}>{bookingConfig.ticketTypes.map((ticket) => <option key={ticket.id} value={ticket.id}>{ticket.name} · {(Number(ticket.priceMinor || 0) / 100).toFixed(2)} {bookingConfig.ticketCurrency}</option>)}</select></label>}
      {hasAttendeeFields && <small>Attendee questions are completed one participant at a time.</small>}
      {bookingFields.filter((field) => field.scope === 'booking').map((field) => <BookingField key={field.key} field={field} value={bookingAnswers[field.key]} onChange={(value) => setBookingAnswers((answers) => ({ ...answers, [field.key]: value }))} />)}
      {bookingFields.filter((field) => field.scope === 'attendee').map((field) => <BookingField key={field.key} field={field} value={attendeeAnswers[field.key]} onChange={(value) => setAttendeeAnswers((answers) => ({ ...answers, [field.key]: value }))} />)}
      {(bookingConfig?.extras || []).map((extra) => <label key={extra.id}>{extra.name} · {(Number(extra.priceMinor || 0) / 100).toFixed(2)} {bookingConfig.ticketCurrency}<input className="input" type="number" min="0" max={extra.maxQuantity || 1} value={extras[extra.id] || 0} onChange={(event) => setExtras((values) => ({ ...values, [extra.id]: Number(event.target.value) }))} /></label>)}
      {bookingFields.some((field) => field.profileBinding && !field.sensitive) && <label className="checkbox-row"><input type="checkbox" checked={saveProfileFields} onChange={(event) => setSaveProfileFields(event.target.checked)} /> Save eligible renewable changes to my profile</label>}
      <button type="button" className="btn btn--primary btn--sm" onClick={() => { const uid = getAuth().currentUser?.uid; startCheckout(qty, { ticketTypeId, selectedExtras: Object.entries(extras).map(([id, quantity]) => ({ id, quantity })), bookingAnswers, attendeeAnswers: uid ? { [uid]: attendeeAnswers } : {}, saveProfileFields }); }} disabled={busy || isPastEvent || Boolean(checkoutUnavailableMessage)}>{isPastEvent ? 'Event ended' : busy ? 'Starting…' : 'Buy ticket'}</button>

      {checkoutUnavailableMessage && <div className="text-muted small" style={{ marginTop: 8 }}>{checkoutUnavailableMessage}</div>}

      {pricing && !isPastEvent && !checkoutUnavailableMessage && (
        <div className="text-muted small" style={{ marginTop: 12, lineHeight: 1.4 }}>
          <div><strong>You pay today:</strong> {totals?.buyerPays || 'Calculating…'}</div>
          {pricing.platformFeeMinor > 0 && <div>Includes platform fee: {totals?.platform}</div>}
          {pricing.addsStripeToBuyer
            ? <div>Includes Stripe fee: {totals?.stripe}</div>
            : <div>Estimated Stripe fee: {totals?.stripe}</div>}
          <div>Ticket price (subtotal): {totals?.unit}</div>
        </div>
      )}
    </>
  );

  return (
    <div className="card" style={{ padding: 16, borderRadius: 12, border: '1px solid #eee' }}>
      {loadError && <div className="error-message" role="alert">{loadError}</div>}
      {!loadError && status === 'paid' && !admissionTicketsEnabled && <div className="status-message" role="status">
        Your booking is confirmed. This event is not currently issuing scannable admission passes.
      </div>}
      {isSkeleton ? (
        <div className="text-muted small">Loading ticket info…</div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Tickets</div>
              <div className="text-muted small">
                {name || 'General Admission'} — {price ? `${price} ${currency || ''}${coverageLabel ? ` ${coverageLabel}` : ''}` : 'Free'}
              </div>
            </div>
            <span className="text-muted small">{label}</span>
          </div>

          {payoutTimingText && <div className="event-payout-timing" role="note" style={{ marginTop: 12 }}>
            <strong>When the club gets paid</strong>
            <span>{payoutTimingText}</span>
          </div>}

          {/* -- Normal buying flow, only if event not ended -- */}
          {status !== 'paid' && !isPastEvent && PurchaseBlock}

          {/* -- After event end notice -- */}
          {isPastEvent && (
            <div className="text-muted small" style={{ marginTop: 12 }}>
              This event has ended. Ticket management and checkout are now closed.
            </div>
          )}

          {/* -- Manage booking only if future event and already paid -- */}
          {status === 'paid' && !isPastEvent && (
            <>
              <div className="text-muted small" style={{ marginTop: 8 }}>
                Manage your booking below.
              </div>

              <div style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  style={{
                    background: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '6px 12px',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                  onClick={() => setManageOpen((v) => !v)}
                  aria-expanded={manageOpen}
                >
                  {manageOpen ? '▾ Hide' : '▸ Manage your booking'}
                </button>

                {manageOpen && (
                  <div style={{ marginTop: 12, borderTop: '1px dashed #ddd', paddingTop: 12 }}>
                    {/* Buy more */}
                    <div style={{ marginBottom: 8 }}>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setOpenSection((s) => (s === 'buy' ? null : 'buy'))}
                        aria-expanded={openSection === 'buy'}
                      >
                        {openSection === 'buy' ? '▾' : '▸'} Buy more
                      </button>
                      {openSection === 'buy' && (
                        <div style={{ padding: '8px 0 0 18px' }}>{PurchaseBlock}</div>
                      )}
                    </div>

                    {/* Refund */}
                    <div>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setOpenSection((s) => (s === 'refund' ? null : 'refund'))}
                        aria-expanded={openSection === 'refund'}
                      >
                        {openSection === 'refund' ? '▾' : '▸'} Request a refund
                      </button>
                      {openSection === 'refund' && (
                        <div style={{ padding: '8px 0 0 18px' }}>
                          <div className="text-muted small" style={{ marginBottom: 8 }}>
                            Start a refund request for your purchased ticket
                            {paidCount > 1 ? 's' : ''}.
                          </div>
                          <button
                            type="button"
                            className="btn btn--secondary btn--sm"
                            onClick={onRequestRefund}
                            disabled={!onRequestRefund || refundRequested}
                            title={
                              refundRequested
                                ? 'Refund already requested'
                                : onRequestRefund
                                ? ''
                                : 'Refunds not available yet'
                            }
                          >
                            {refundRequested
                              ? 'Refund requested'
                              : onRequestRefund
                              ? 'Start refund request'
                              : 'Refunds coming soon'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
